import React, { useState, useCallback, useMemo } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { MessageFilters as MessageFiltersType, MessageStats } from '../../types/messages';
import './MessageFilters.css';
import './MessageAccessibility.css';

interface MessageFiltersProps {
  filters: MessageFiltersType;
  onFiltersChange: (filters: MessageFiltersType) => void;
  messageStats: MessageStats;
  loading?: boolean;
}

interface FilterOption {
  value: string;
  label: string;
  count?: number;
}

/**
 * Enhanced MessageFilters component with improved layout and functionality
 * Requirements: 6.1, 6.2, 6.3, 6.4 - Performance optimizations
 */
export const MessageFilters: React.FC<MessageFiltersProps> = React.memo(({
  filters,
  onFiltersChange,
  messageStats,
  loading = false
}) => {
  const { t } = useLanguage();

  // Memoized read status filter options with counts
  const readStatusOptions: FilterOption[] = useMemo(() => [
    { value: 'all', label: t('messagesPage.filterAll'), count: messageStats.total },
    { value: 'unread', label: t('messagesPage.filterUnread'), count: messageStats.unread },
    { value: 'read', label: t('messagesPage.filterRead'), count: messageStats.read }
  ], [messageStats, t]);

  // Memoized type filter options with counts
  const typeOptions: FilterOption[] = useMemo(() => [
    { value: 'all', label: t('messagesPage.filterAllTypes'), count: messageStats.total },
    { value: 'info', label: t('messagesPage.typeInfo'), count: messageStats.byType.info || 0 },
    { value: 'welcome', label: t('messagesPage.typeWelcome'), count: messageStats.byType.welcome || 0 },
    { value: 'announcement', label: t('messagesPage.typeAnnouncement'), count: messageStats.byType.announcement || 0 },
    { value: 'maintenance', label: t('messagesPage.typeMaintenance'), count: messageStats.byType.maintenance || 0 },
    { value: 'task_pending_review', label: t('messagesPage.typeTaskPending'), count: messageStats.byType.task_pending_review || 0 },
    { value: 'task_approved', label: t('messagesPage.typeTaskApproved'), count: messageStats.byType.task_approved || 0 },
    { value: 'task_rejected', label: t('messagesPage.typeTaskRejected'), count: messageStats.byType.task_rejected || 0 },
    { value: 'survey_completed', label: t('messagesPage.typeSurveyCompleted'), count: messageStats.byType.survey_completed || 0 },
    { value: 'project_release', label: t('messagesPage.typeProjectRelease'), count: messageStats.byType.project_release || 0 },
    { value: 'team_application_received', label: t('messagesPage.typeTeamAppReceived'), count: messageStats.byType.team_application_received || 0 },
    { value: 'team_application_approved', label: t('messagesPage.typeTeamAppApproved'), count: messageStats.byType.team_application_approved || 0 },
    { value: 'team_application_rejected', label: t('messagesPage.typeTeamAppRejected'), count: messageStats.byType.team_application_rejected || 0 },
    { value: 'team_formed', label: t('messagesPage.typeTeamFormed'), count: messageStats.byType.team_formed || 0 },
    { value: 'contact_form', label: t('messagesPage.typeContactForm'), count: messageStats.byType.contact_form || 0 },
    { value: 'contact_reply', label: t('messagesPage.typeContactReply'), count: messageStats.byType.contact_reply || 0 }
  ], [messageStats.byType, messageStats.total, t]);

  // Calculate active filter count
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.readStatus !== 'all') count++;
    if (filters.type !== 'all') count++;
    if (filters.source !== 'all') count++;
    if (filters.bookmarked) count++;
    return count;
  }, [filters]);

  // Handle filter changes with proper validation
  const handleReadStatusChange = useCallback((readStatus: string) => {
    // Validate readStatus value
    if (['all', 'unread', 'read'].includes(readStatus)) {
      onFiltersChange({
        ...filters,
        readStatus: readStatus as MessageFiltersType['readStatus']
      });
    }
  }, [filters, onFiltersChange]);

  const handleTypeChange = useCallback((type: string) => {
    // Validate type value
    if (['all', 'info', 'welcome', 'announcement', 'maintenance', 'task_pending_review', 'task_approved', 'task_rejected', 'project_release', 'survey_completed', 'team_application_received', 'team_application_approved', 'team_application_rejected', 'team_formed', 'contact_form', 'contact_reply'].includes(type)) {
      onFiltersChange({
        ...filters,
        type: type as MessageFiltersType['type']
      });
    }
  }, [filters, onFiltersChange]);

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    onFiltersChange({
      readStatus: 'all',
      type: 'all',
      source: 'all',
      dateRange: {}
    });
  }, [onFiltersChange]);

  return (
    <div className={`message-filters-horizontal ${loading ? 'loading' : ''}`}>
      {/* Simplified Filters - Clean and Concise */}
      <div className="primary-filters">
        <div className="filter-group-inline">
          <label className="filter-label-compact" htmlFor="read-status-filter">
            <i className="bi bi-funnel" aria-hidden="true"></i>
            {t('messagesPage.status')}
          </label>
          <select
            id="read-status-filter"
            value={filters.readStatus}
            onChange={(e) => handleReadStatusChange(e.target.value)}
            className="filter-select-compact"
            disabled={loading}
            title="Filter messages by read/unread status"
          >
            {readStatusOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label} ({option.count || 0})
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group-inline">
          <label className="filter-label-compact" htmlFor="type-filter">
            <i className="bi bi-tag" aria-hidden="true"></i>
            {t('messagesPage.type')}
          </label>
          <select
            id="type-filter"
            value={filters.type}
            onChange={(e) => handleTypeChange(e.target.value)}
            className="filter-select-compact"
            disabled={loading}
            title="Filter messages by their type or category"
          >
            {typeOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label} ({option.count || 0})
              </option>
            ))}
          </select>
        </div>

        {/* Clear All Filters */}
        {activeFilterCount > 0 && (
          <button
            type="button"
            className="clear-all-btn"
            onClick={clearAllFilters}
            disabled={loading}
            title="Clear all active filters"
            aria-label="Clear all active filters"
          >
            <i className="bi bi-x-circle" aria-hidden="true"></i>
            {t('messagesPage.clearAll')}
          </button>
        )}
      </div>
    </div>
  );
});

export default MessageFilters;